import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { fmtDateTime } from "@/lib/format";
import {
  Shield,
  Wrench,
  UserPlus,
  Trash2,
  Save,
  Edit3,
  X,
  Search,
  AlertTriangle,
  KeyRound,
} from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";
import PasswordDisplayModal from "@/components/PasswordDisplayModal";

const ROLES = [
  {
    value: "admin",
    label: "Admin",
    description: "Accesso totale al backoffice, gestione staff e clienti.",
    icon: Shield,
    accent: "from-blue-500 to-purple-600",
    textAccent: "text-blue-700 bg-blue-50 border-blue-200",
  },
  {
    value: "editor",
    label: "Operativo",
    description: "Backoffice operativo: caricamento dati, report, news, notifiche.",
    icon: Wrench,
    accent: "from-emerald-500 to-teal-600",
    textAccent: "text-emerald-700 bg-emerald-50 border-emerald-200",
  },
];

export default function AdminStaff() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState({ full_name: "", email: "", password: "", role: "editor" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: "", email: "", password: "", role: "editor" });
  const [confirmState, setConfirmState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pwModal, setPwModal] = useState(null);

  const generatePassword = () => {
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    const symbols = "!@#$%*?";
    let pw = "";
    for (let i = 0; i < 10; i++) pw += alphabet[Math.floor(Math.random() * alphabet.length)];
    pw += symbols[Math.floor(Math.random() * symbols.length)];
    pw += symbols[Math.floor(Math.random() * symbols.length)];
    return pw.split("").sort(() => Math.random() - 0.5).join("");
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setUsers((data || []).filter((u) => u.role === "admin" || u.role === "editor"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter !== "all" && u.role !== roleFilter) return false;
      if (!term) return true;
      return (
        (u.full_name || "").toLowerCase().includes(term) ||
        (u.email || "").toLowerCase().includes(term)
      );
    });
  }, [users, q, roleFilter]);

  const counts = useMemo(
    () => ({
      admin: users.filter((u) => u.role === "admin").length,
      editor: users.filter((u) => u.role === "editor").length,
    }),
    [users],
  );

  const createStaff = async () => {
    if (!newForm.full_name.trim() || !newForm.email.trim() || !newForm.password) {
      return toast.error("Compila nome, email e password");
    }
    if (newForm.password.length < 6) {
      return toast.error("La password deve avere almeno 6 caratteri");
    }
    try {
      await api.post("/users", {
        email: newForm.email.trim(),
        password: newForm.password,
        full_name: newForm.full_name.trim(),
        role: newForm.role,
      });
      const createdPw = newForm.password;
      const createdEmail = newForm.email.trim();
      const createdName = newForm.full_name.trim();
      setPwModal({
        user: { email: createdEmail, full_name: createdName },
        password: createdPw,
        mode: "create",
      });
      setNewForm({ full_name: "", email: "", password: "", role: "editor" });
      setShowNew(false);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore creazione account"); }
  };

  const resetStaffPassword = (u) => {
    setConfirmState({
      title: "Reset password?",
      message: `Verrà generata una nuova password sicura per "${u.full_name || u.email}". Sarà mostrata una sola volta.`,
      confirmLabel: "Genera nuova password",
      variant: "primary",
      onConfirm: async () => {
        try {
          const { data } = await api.post(`/users/${u.id}/reset-password`);
          setPwModal({
            user: { email: data.email, full_name: data.full_name },
            password: data.password,
            mode: "reset-auto",
          });
          await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Errore reset password"); }
      },
    });
  };

  const startEdit = (u) => {
    setEditingId(u.id);
    setEditForm({ full_name: u.full_name || "", email: u.email || "", password: "", role: u.role });
  };
  const saveEdit = async () => {
    const payload = {};
    if (editForm.full_name?.trim()) payload.full_name = editForm.full_name.trim();
    if (editForm.email?.trim()) payload.email = editForm.email.trim();
    if (editForm.password?.trim()) payload.password = editForm.password;
    if (editForm.role) payload.role = editForm.role;
    if (Object.keys(payload).length === 0) {
      setEditingId(null);
      return;
    }
    try {
      await api.patch(`/users/${editingId}`, payload);
      if (payload.password) {
        const target = users.find((u) => u.id === editingId);
        setPwModal({
          user: { email: payload.email || target?.email, full_name: payload.full_name || target?.full_name },
          password: payload.password,
          mode: "reset",
        });
      } else {
        toast.success("Account aggiornato");
      }
      setEditingId(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore aggiornamento"); }
  };

  const removeStaff = (u) => {
    if (u.id === me?.id) {
      toast.error("Non puoi eliminare il tuo stesso account");
      return;
    }
    setConfirmState({
      title: `Eliminare l'account ${u.role === "admin" ? "Admin" : "Operativo"}?`,
      message: `"${u.full_name || u.email}" perderà l'accesso al portale. L'operazione è irreversibile.`,
      confirmLabel: "Elimina",
      variant: "danger",
      onConfirm: async () => {
        try {
          await api.delete(`/users/${u.id}`);
          toast.success("Account eliminato");
          await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
      },
    });
  };

  const roleMeta = (r) => ROLES.find((x) => x.value === r) || ROLES[1];

  return (
    <div className="space-y-5" data-testid="admin-staff-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Gestione Staff</h1>
          <p className="text-sm text-slate-500 mt-1">
            Crea e gestisci gli account <strong>Admin</strong> (accesso totale) e <strong>Operativi</strong> (backoffice).
          </p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="btn-brand flex items-center gap-2"
          data-testid="new-staff-btn"
        >
          <UserPlus size={16} /> Nuovo account
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {ROLES.map((r) => {
          const Icon = r.icon;
          return (
            <div key={r.value} className="card-elevated p-5 flex items-center gap-4" data-testid={`staff-count-${r.value}`}>
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${r.accent} flex items-center justify-center shadow-lg`}>
                <Icon className="text-white" size={22} />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{r.label}</div>
                <div className="font-display font-bold text-2xl text-slate-900 mt-0.5">{counts[r.value] || 0}</div>
                <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{r.description}</div>
              </div>
            </div>
          );
        })}
      </div>

      {showNew && (
        <div className="card-elevated p-5 space-y-3" data-testid="new-staff-form">
          <div className="flex items-center justify-between">
            <div className="font-display font-semibold">Nuovo account backoffice</div>
            <button onClick={() => setShowNew(false)} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {ROLES.map((r) => {
              const Icon = r.icon;
              const active = newForm.role === r.value;
              return (
                <button
                  key={r.value}
                  onClick={() => setNewForm({ ...newForm, role: r.value })}
                  className={`text-left p-3 rounded-xl border transition ${active ? "border-blue-500 bg-blue-50/40 ring-2 ring-blue-100" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid={`role-choice-${r.value}`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${r.accent} flex items-center justify-center`}>
                      <Icon size={16} className="text-white" />
                    </div>
                    <div className="font-semibold text-sm">{r.label}</div>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1.5">{r.description}</div>
                </button>
              );
            })}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              value={newForm.full_name}
              onChange={(e) => setNewForm({ ...newForm, full_name: e.target.value })}
              placeholder="Nome e cognome"
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
              data-testid="new-staff-name"
            />
            <input
              value={newForm.email}
              onChange={(e) => setNewForm({ ...newForm, email: e.target.value })}
              placeholder="Email"
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
              data-testid="new-staff-email"
            />
            <div className="flex gap-1">
              <input
                type="text"
                value={newForm.password}
                onChange={(e) => setNewForm({ ...newForm, password: e.target.value })}
                placeholder="Password (min. 6)"
                className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500 font-mono"
                data-testid="new-staff-password"
              />
              <button
                type="button"
                onClick={() => setNewForm({ ...newForm, password: generatePassword() })}
                className="px-2.5 py-2 rounded-lg border border-slate-200 text-xs text-slate-600 hover:border-blue-500 hover:text-blue-600 whitespace-nowrap"
                title="Genera password sicura"
                data-testid="new-staff-generate-pw"
              >
                Genera
              </button>
            </div>
          </div>
          <div className="text-[11px] text-slate-500 flex items-start gap-1.5">
            <AlertTriangle size={11} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <span>La password sarà mostrata una sola volta al termine della creazione. Copiala e condividila con l&apos;utente tramite un canale sicuro.</span>
          </div>
          <button onClick={createStaff} className="btn-brand flex items-center gap-2" data-testid="new-staff-save">
            <Save size={14} /> Crea account
          </button>
        </div>
      )}

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per nome o email…"
            className="w-full pl-10 pr-9 py-2.5 rounded-xl border border-slate-200 text-sm bg-white outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            data-testid="staff-search"
          />
          {q && (
            <button
              onClick={() => setQ("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="role-filter">
          {[{ v: "all", l: "Tutti" }, { v: "admin", l: "Admin" }, { v: "editor", l: "Operativi" }].map((f) => (
            <button
              key={f.v}
              onClick={() => setRoleFilter(f.v)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${roleFilter === f.v ? "bg-white shadow text-blue-600" : "text-slate-500"}`}
              data-testid={`role-filter-${f.v}`}
            >
              {f.l}
            </button>
          ))}
        </div>
      </div>

      <div className="card-elevated overflow-hidden" data-testid="staff-list">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">
            {q || roleFilter !== "all" ? "Nessun risultato per i filtri applicati." : "Nessun account backoffice registrato."}
          </div>
        ) : (
          <table className="data-table w-full">
            <thead>
              <tr>
                <th>Ruolo</th><th>Nome</th><th>Email</th><th>Creato</th><th className="text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const rm = roleMeta(u.role);
                const isEditing = editingId === u.id;
                const isSelf = u.id === me?.id;
                return (
                  <tr key={u.id} data-testid={`staff-row-${u.id}`}>
                    {isEditing ? (
                      <td colSpan={5} className="!p-3">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                          <select
                            value={editForm.role}
                            onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                            disabled={isSelf}
                            className="px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500 disabled:opacity-60"
                            data-testid={`edit-staff-role-${u.id}`}
                          >
                            <option value="admin">Admin</option>
                            <option value="editor">Operativo</option>
                          </select>
                          <input
                            value={editForm.full_name}
                            onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                            placeholder="Nome e cognome"
                            className="px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                            data-testid={`edit-staff-name-${u.id}`}
                          />
                          <input
                            value={editForm.email}
                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                            placeholder="Email"
                            className="px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                            data-testid={`edit-staff-email-${u.id}`}
                          />
                          <input
                            type="password"
                            value={editForm.password}
                            onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                            placeholder="Nuova password (vuoto = invariata)"
                            className="px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-blue-500"
                            data-testid={`edit-staff-password-${u.id}`}
                          />
                        </div>
                        <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                          <AlertTriangle size={12} className="text-amber-500" />
                          <span>
                            La password originale non è mai visibile (hash bcrypt). Se necessario, imposta qui una nuova password.
                            {isSelf && " · Non puoi modificare il tuo stesso ruolo."}
                          </span>
                        </div>
                        <div className="mt-3 flex items-center gap-2">
                          <button onClick={saveEdit} className="btn-brand text-sm !py-1.5 !px-3 flex items-center gap-1.5" data-testid={`save-staff-${u.id}`}>
                            <Save size={14} /> Salva
                          </button>
                          <button onClick={() => setEditingId(null)} className="text-sm px-3 py-1.5 text-slate-500 hover:text-slate-700">Annulla</button>
                        </div>
                      </td>
                    ) : (
                      <>
                        <td>
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${rm.textAccent}`}>
                            <rm.icon size={11} /> {rm.label}
                          </span>
                        </td>
                        <td className="!font-sans font-semibold">
                          {u.full_name} {isSelf && <span className="ml-1 text-[10px] uppercase tracking-widest text-slate-400">tu</span>}
                        </td>
                        <td className="!font-sans text-slate-600">{u.email}</td>
                        <td className="!font-sans text-xs text-slate-500">{fmtDateTime(u.created_at)}</td>
                        <td className="text-right whitespace-nowrap">
                          <button
                            onClick={() => resetStaffPassword(u)}
                            className="p-1.5 text-slate-500 hover:text-purple-600 mr-1"
                            title="Genera nuova password"
                            data-testid={`reset-pw-staff-${u.id}`}
                          >
                            <KeyRound size={15} />
                          </button>
                          <button
                            onClick={() => startEdit(u)}
                            className="p-1.5 text-slate-500 hover:text-blue-600 mr-1"
                            title="Modifica"
                            data-testid={`edit-staff-${u.id}`}
                          >
                            <Edit3 size={15} />
                          </button>
                          <button
                            onClick={() => removeStaff(u)}
                            disabled={isSelf}
                            className="p-1.5 text-red-500 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed"
                            title={isSelf ? "Non puoi eliminarti" : "Elimina"}
                            data-testid={`remove-staff-${u.id}`}
                          >
                            <Trash2 size={15} />
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
      <PasswordDisplayModal
        open={!!pwModal}
        onClose={() => setPwModal(null)}
        user={pwModal?.user}
        password={pwModal?.password}
        mode={pwModal?.mode}
      />
    </div>
  );
}
